// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Location detail page route module.
 * Provides the main interface for viewing and managing a specific location.
 *
 * Features:
 * - Location information display
 * - Vertical navigation for different sections
 * - Archive status management
 * - Edit capabilities
 */
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ArchiveControl } from '@/components/archive-control';
import { Entity404 } from '@/components/entity-404';
import { IntegrationStatus } from '@/components/integration-status';
import { Link } from '@/components/link';
import { SupportErrorBoundary } from '@/components/support-error-boundary';
import { VerticalNavigationLink } from '@/components/vertical-navigation-link';
import { EditRounded, MergeRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { updateLocation } from '@/location/api/update-location';
import { EditLocationDialog } from '@/location/components/edit-location-dialog';
import { LocationErrorBoundary } from '@/location/components/location-error-boundary';
import { MergeLocationDialog } from '@/location/components/merge-location-dialog';
import { SiteAddressLabel } from '@/location/components/site-address-label';
import { useLocation } from '@/location/hooks/use-location';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import {
  Box,
  Breadcrumbs,
  Chip,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Outlet, createFileRoute, useRouter } from '@tanstack/react-router';
import { Suspense } from 'react';

export const Route = createFileRoute('/_app/locations/$locationKey')({
  component: LocationRoute,
  notFoundComponent: LocationNotFound,
});

function LocationRoute() {
  const { locationKey } = Route.useParams();

  return (
    <LocationErrorBoundary>
      <LocationContent locationKey={locationKey} />
    </LocationErrorBoundary>
  );
}

function LocationNotFound() {
  return (
    <Entity404
      listPath={{
        to: '/locations',
      }}
      listLabel="Go to locations"
    />
  );
}

function LocationContent(props: { locationKey: string }) {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const editDialog = useDisclosure();
  const mergeDialog = useDisclosure();
  const router = useRouter();

  const { location } = useLocation(props.locationKey);

  const updateLocationMutation = useMutation({
    mutationFn: (isArchived: boolean) => {
      return updateLocation(props.locationKey, location.type, {
        isArchived,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['location', props.locationKey],
      });
    },
  });

  if (
    !router.state.matches.some(
      (node) => node.pathname === router.state.location.pathname
    )
  ) {
    return (
      <Entity404
        listPath={{
          to: '/locations/$locationKey',
          params: { locationKey: props.locationKey },
        }}
        listLabel={`Back to ${getEffectiveLocationName(location)}`}
      />
    );
  }

  // Determine display values
  // Sites display their core name; zones include the parent prefix (e.g. "Parent — Zone")
  const displayName =
    location.type === 'ZONE'
      ? getEffectiveLocationName(location)
      : location.name;
  const companyName = location.customer?.name ?? user.organization.name;
  const archiveToggleTooltip = location.isArchived
    ? 'Unarchive location'
    : 'Archive location. Archiving does not change your location, but archived locations are hidden by default in the location list.';

  return (
    <Stack>
      <EditLocationDialog
        location={location}
        isOpen={editDialog.isOpen}
        onClose={editDialog.close}
      />
      {mergeDialog.isOpen && (
        <MergeLocationDialog
          location={location}
          isOpen={mergeDialog.isOpen}
          onClose={mergeDialog.close}
        />
      )}
      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          paddingX={3}
          marginTop={3}
        >
          <Breadcrumbs separator="›" sx={{ py: 0.75 }}>
            <Link
              to="/locations"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block' }}
            >
              Locations
            </Link>
            <Typography variant="body2" color="text.secondary">
              {displayName}
            </Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            {!location.isTransient && (
              <Tooltip arrow title={archiveToggleTooltip}>
                <span>
                  <ArchiveControl
                    isArchived={location.isArchived}
                    isDisabled={updateLocationMutation.isPending}
                    onChange={updateLocationMutation.mutate}
                  />
                </span>
              </Tooltip>
            )}
            <IconButton size="small" onClick={mergeDialog.open}>
              <MergeRounded fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={editDialog.open}>
              <EditRounded fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Stack paddingX={{ xs: 1, md: 3 }} spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {location.integrations.length > 0 && (
              <IntegrationStatus
                status={location.integrations}
                entityType="location"
                entityKey={props.locationKey}
                entityDisplayName={displayName}
                onResolved={() => {
                  // Prefix-invalidate so both this detail page and
                  // the listing pages refetch — the hook-level
                  // invalidation only hits ['location', thisKey].
                  void queryClient.invalidateQueries({
                    queryKey: ['location'],
                  });
                }}
              />
            )}
            <Typography variant="h5">{displayName}</Typography>
            {location.isArchived && <Chip size="small" label="Archived" />}
            {location.isTransient && (
              <Chip size="small" label="Transient" variant="outlined" />
            )}
            {location.isThirdPartyEnabled && <Chip size="small" label="3PL" />}
          </Stack>
          <Stack direction="row" spacing={4} marginTop={2}>
            <Box display="grid" gridTemplateColumns="auto 1fr" gap={1}>
              {!location.isTransient && (
                <>
                  <Typography variant="subtitle2">Company</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {companyName}
                  </Typography>
                </>
              )}
              <Typography variant="subtitle2">Address</Typography>
              <SiteAddressLabel site={location} variant="body2" />
              {!location.isTransient && (
                <>
                  <Typography variant="subtitle2">
                    Include in inventory
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {location.isInventory ? 'Yes' : 'No'}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          marginY={3}
          paddingX={{ xs: 1, md: 3 }}
          direction={{ xs: 'column', md: 'row' }}
        >
          <Stack
            direction={{ xs: 'row', md: 'column' }}
            borderTop="1px solid transparent"
          >
            {location.type === 'SITE' && (
              <>
                <VerticalNavigationLink
                  to="/locations/$locationKey"
                  params={{ locationKey: props.locationKey }}
                  activeOptions={{ exact: true, includeSearch: false }}
                >
                  Zones
                </VerticalNavigationLink>
                <VerticalNavigationLink
                  to="/locations/$locationKey/related-assets"
                  params={{ locationKey: props.locationKey }}
                >
                  Related assets
                </VerticalNavigationLink>
              </>
            )}
            {location.type === 'ZONE' && (
              <VerticalNavigationLink
                to="/locations/$locationKey"
                params={{ locationKey: props.locationKey }}
                activeOptions={{ exact: true, includeSearch: false }}
              >
                Related assets
              </VerticalNavigationLink>
            )}
            <VerticalNavigationLink
              to="/locations/$locationKey/related-projects"
              params={{ locationKey: props.locationKey }}
            >
              Related projects
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/locations/$locationKey/related-shipments"
              params={{ locationKey: props.locationKey }}
            >
              Related shipments
            </VerticalNavigationLink>
            <VerticalNavigationLink
              to="/locations/$locationKey/audit"
              params={{ locationKey: props.locationKey }}
            >
              Audit log
            </VerticalNavigationLink>
          </Stack>
          <Box flexGrow={1} paddingLeft={{ xs: 0, md: 2 }}>
            <SupportErrorBoundary message="An error occurred.">
              <Suspense>
                <Outlet />
              </Suspense>
            </SupportErrorBoundary>
          </Box>
        </Stack>
      </Container>
    </Stack>
  );
}
